import $ from 'jquery';

import * as LoadingScreen from 'ui-library/LoadingScreen';
import * as Net from 'util/Net';

/**
 * jQuery's `@types` define `.done()` callbacks as `(value?: T) => void`, which incorrectly marks
 * successful responses as optional. Use this return type on DAO methods where callers need
 * non-optional success values in `.done()` handlers.
 */
export interface BetterJQueryPromise<T> extends JQueryPromise<T> {
  done(doneCallback: (value: T, ...args: Array<unknown>) => void): BetterJQueryPromise<T>;
  then<U>(
    doneFilter: (value: T, ...values: Array<unknown>) => U | JQueryPromise<U>,
    failFilter?: (...reasons: Array<unknown>) => unknown,
    progressFilter?: (...progression: Array<unknown>) => unknown
  ): JQueryPromise<U>;
}

export type MaybePromise<T> = T | Promise<T>;

export async function withLoadingScreen<T>(promise: Promise<T>, message: string | null = null): Promise<T> {
  LoadingScreen.show(message);
  try {
    return await promise;
  } finally {
    LoadingScreen.hide();
  }
}

/**
 * If `result` is a Promise, wraps the promise in `withLoadingScreen`. Otherwise, no-ops.
 *
 * This is helpful when it is important to preserve the (a)synchronous resolution of `result`.
 */
export function maybeWithLoadingScreen<T>(
  result: MaybePromise<T>,
  message: string | null = null
): MaybePromise<T> {
  if (result instanceof Promise) {
    return withLoadingScreen(result, message);
  } else {
    return result;
  }
}

/**
 * If `result` is a Promise, calls `callback` asynchronously. Otherwise, calls it synchronously.
 */
export function runAfterResolve<T, U>(result: MaybePromise<T>, callback: (result: T) => U): MaybePromise<U> {
  if (result instanceof Promise) {
    return result.then((actualResult: T) => callback(actualResult));
  } else {
    return callback(result);
  }
}

export function asJQueryPromise<T>(promise: Promise<T>): JQueryPromise<T> {
  const deferred: JQueryDeferred<T> = $.Deferred();
  promise
    .then(
      (val) => {
        deferred.resolve(val);
      },
      (val) => {
        deferred.reject(val);
      }
    )
    .catch((err) => {
      if (Net.isServerRequestError(err)) {
        deferred.reject(err);
      }
      throw err;
    });
  return deferred.promise();
}

/**
 * Resolves after `ms` milliseconds, or rejects with AbortError if `signal` is aborted first.
 */
export function abortableDelay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const id = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    function onAbort(): void {
      clearTimeout(id);
      reject(new DOMException('Aborted', 'AbortError'));
    }
    signal.addEventListener('abort', onAbort, {once: true});
  });
}
